import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { DEFAULT_THEME, type ThemeName, isDarkTheme, themeCssVars } from './themes';

const STORAGE_KEY = 'throughline:theme';

interface ThemeContextValue {
  theme: ThemeName;
  setTheme: (name: ThemeName) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Exported (additive — no behavior change) so ThemeProvider.test.ts can prove
 *  the "fresh, never-picked load resolves to DEFAULT_THEME" behavior directly
 *  against the SAME function the component calls, rather than only testing
 *  the DEFAULT_THEME constant in isolation. Mirrors this app's onboardingStep
 *  .test.ts idiom: a pure read function, independently testable via an
 *  in-memory localStorage stub, no jsdom/component-render dependency needed.
 *
 * LANE U6 MIGRATION SAFETY: a user whose localStorage still carries one of
 * the three now-retired keys ('warm-paper' / 'sage-calm' / 'dusk' — see
 * docs/design/palette-archive-2026-07-05.md for their preserved values) is
 * treated exactly like an invalid/corrupt value below — this function
 * returns null, and the caller's `?? DEFAULT_THEME` fallback resolves them
 * cleanly to Sage Teal (the new default) rather than crashing or rendering
 * with an undefined theme. Never a broken state. */
export function readStoredTheme(): ThemeName | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'sage-teal' || raw === 'ember-gray') return raw;
    return null;
  } catch {
    // localStorage can throw in locked-down/private-mode contexts — theme just
    // falls back to the default rather than crashing the app.
    return null;
  }
}

/**
 * Theme is a LOCAL preference only (per the contract: "themes → local
 * preference") — never round-tripped to the server. Persists to
 * localStorage so a reload keeps the pick; a fresh, never-picked load falls
 * back to themes.ts's DEFAULT_THEME (sage-teal — LANE U6, Jason's palette
 * pick, replacing the prior sage-calm default).
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(() => readStoredTheme() ?? DEFAULT_THEME);

  const setTheme = useCallback((name: ThemeName) => {
    setThemeState(name);
    try {
      window.localStorage.setItem(STORAGE_KEY, name);
    } catch {
      // Best-effort persistence only.
    }
  }, []);

  // Apply the CSS variable set to :root so every screen (including anything
  // portalled outside the app shell, e.g. a future modal) sees the theme.
  useEffect(() => {
    const vars = themeCssVars(theme);
    const root = document.documentElement;
    for (const [key, value] of Object.entries(vars)) {
      root.style.setProperty(key, value);
    }
    root.dataset.tlTheme = theme;
    root.style.colorScheme = isDarkTheme(theme) ? 'dark' : 'light';
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, isDark: isDarkTheme(theme) }),
    [theme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
